import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Play, Plus, Minus, Target, Calendar, Clock, Pencil, Check, X } from 'lucide-react';

export default function PracticeSession() {
  const navigate = useNavigate();
  // View mode: 'dashboard' or 'tracker'
  const [viewMode, setViewMode] = useState('dashboard');
  
  // Session state
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [previousSessions, setPreviousSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(true);
  
  // Goals state
  const [goals, setGoals] = useState({ two_pt_goal: 50, three_pt_goal: 30, ft_goal: 70 });
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [editingGoals, setEditingGoals] = useState({ two_pt_goal: 50, three_pt_goal: 30, ft_goal: 70 });
  const [savingGoals, setSavingGoals] = useState(false);
  
  // Shot counters
  const [stats, setStats] = useState({
    two_pt_made: 0,
    two_pt_attempted: 0,
    three_pt_made: 0,
    three_pt_attempted: 0,
    ft_made: 0,
    ft_attempted: 0,
  });
  
  const [saving, setSaving] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchPreviousSessions();
    fetchGoals();
  }, []);

  const fetchPreviousSessions = async () => {
    try {
      const response = await api.get('/practice/sessions?limit=20');
      setPreviousSessions(response.data);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoadingSessions(false);
    }
  };

  const fetchGoals = async () => {
    try {
      const response = await api.get('/practice/goals');
      setGoals({
        two_pt_goal: response.data.two_pt_goal,
        three_pt_goal: response.data.three_pt_goal,
        ft_goal: response.data.ft_goal,
      });
      setEditingGoals({
        two_pt_goal: response.data.two_pt_goal,
        three_pt_goal: response.data.three_pt_goal,
        ft_goal: response.data.ft_goal,
      });
    } catch (error) {
      console.error('Failed to fetch goals:', error);
    }
  };

  const saveGoals = async () => {
    setSavingGoals(true);
    try {
      await api.post('/practice/goals', editingGoals);
      setGoals({ ...editingGoals });
      setShowGoalModal(false);
      toast.success('Practice goals updated!');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Failed to save goals'));
    } finally {
      setSavingGoals(false);
    }
  };

  // Start a new practice session
  const startSession = async () => {
    try {
      const response = await api.post('/practice/start');
      setSessionId(response.data.session_id);
      setSessionStartTime(new Date(response.data.started_at));
      setSessionStarted(true);
      setStats({
        two_pt_made: 0,
        two_pt_attempted: 0,
        three_pt_made: 0,
        three_pt_attempted: 0,
        ft_made: 0,
        ft_attempted: 0,
      });
      setViewMode('tracker');
      toast.success('Practice session started!');
    } catch (error) {
      console.error('Failed to start session:', error);
      toast.error(apiErrorMessage(error, 'Failed to start session'));
    }
  };

  // Record a make (+1 made, +1 attempted)
  const recordMake = (shotType) => {
    if (!sessionStarted) return;
    setStats(prev => {
      const newStats = { ...prev };
      if (shotType === '2PT') {
        newStats.two_pt_made += 1;
        newStats.two_pt_attempted += 1;
      } else if (shotType === '3PT') {
        newStats.three_pt_made += 1;
        newStats.three_pt_attempted += 1;
      } else if (shotType === 'FT') {
        newStats.ft_made += 1;
        newStats.ft_attempted += 1;
      }
      return newStats;
    });
  };

  // Record a miss (+1 attempted only)
  const recordMiss = (shotType) => {
    if (!sessionStarted) return;
    setStats(prev => {
      const newStats = { ...prev };
      if (shotType === '2PT') {
        newStats.two_pt_attempted += 1;
      } else if (shotType === '3PT') {
        newStats.three_pt_attempted += 1;
      } else if (shotType === 'FT') {
        newStats.ft_attempted += 1;
      }
      return newStats;
    });
  };

  // Save session to backend
  const saveSession = async () => {
    if (!sessionId) return;
    setSaving(true);
    try {
      await api.patch(`/practice/${sessionId}`, {
        two_pt_made: stats.two_pt_made,
        two_pt_attempted: stats.two_pt_attempted,
        three_pt_made: stats.three_pt_made,
        three_pt_attempted: stats.three_pt_attempted,
        ft_made: stats.ft_made,
        ft_attempted: stats.ft_attempted,
      });
      toast.success('Session saved!');
    } catch (error) {
      console.error('Failed to save session:', error);
      toast.error(apiErrorMessage(error, 'Failed to save session'));
    } finally {
      setSaving(false);
    }
  };

  // End session and return to dashboard
  const endSession = async () => {
    await saveSession();
    setSessionStarted(false);
    setSessionId(null);
    setViewMode('dashboard');
    fetchPreviousSessions(); // Refresh sessions list
  };

  // Auto-save every 30 seconds if session is active
  useEffect(() => {
    if (!sessionStarted || !sessionId) return;
    const interval = setInterval(() => {
      saveSession();
    }, 30000);
    return () => clearInterval(interval);
  }, [sessionStarted, sessionId, stats]);

  // Calculate percentage
  const calcPct = (made, attempted) => {
    if (attempted === 0) return null;
    return Math.round((made / attempted) * 100);
  };

  // Check if stat meets goal
  const meetsGoal = (pct, goal) => {
    if (pct === null) return null;
    return pct >= goal;
  };

  // Shot row component for tracker
  const ShotRow = ({ label, made, attempted, shotType }) => (
    <div className="flex items-center justify-between py-6 border-b border-slate-100 last:border-b-0">
      <div className="flex items-center gap-6">
        <span className="text-xl font-bold text-slate-900 w-12">{label}</span>
        <div className="text-center">
          <span className="text-5xl font-bold text-slate-900 tabular-nums">{made}</span>
          <span className="text-2xl text-slate-400 mx-1">/</span>
          <span className="text-3xl font-bold text-slate-400 tabular-nums">{attempted}</span>
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <button
          onClick={() => recordMake(shotType)}
          className="w-12 h-12 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white flex items-center justify-center transition-colors shadow-md active:scale-95"
          data-testid={`${shotType.toLowerCase()}-make-btn`}
        >
          <Plus className="w-6 h-6" strokeWidth={3} />
        </button>
        <button
          onClick={() => recordMiss(shotType)}
          className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors shadow-md active:scale-95"
          data-testid={`${shotType.toLowerCase()}-miss-btn`}
        >
          <Minus className="w-6 h-6" strokeWidth={3} />
        </button>
      </div>
    </div>
  );

  // Session card with goal comparison
  const SessionCard = ({ session }) => {
    const sessionDate = new Date(session.started_at);
    const totalMade = session.two_pt_made + session.three_pt_made + session.ft_made;
    const totalAttempted = session.two_pt_attempted + session.three_pt_attempted + session.ft_attempted;
    
    const twoPct = calcPct(session.two_pt_made, session.two_pt_attempted);
    const threePct = calcPct(session.three_pt_made, session.three_pt_attempted);
    const ftPct = calcPct(session.ft_made, session.ft_attempted);
    
    const twoMet = meetsGoal(twoPct, goals.two_pt_goal);
    const threeMet = meetsGoal(threePct, goals.three_pt_goal);
    const ftMet = meetsGoal(ftPct, goals.ft_goal);
    
    return (
      <div 
        className="bg-white border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors"
        data-testid={`session-${session.session_id}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Calendar className="w-4 h-4" />
            <span className="font-medium">
              {sessionDate.toLocaleDateString('en-US', { 
                weekday: 'short',
                month: 'short', 
                day: 'numeric'
              })}
            </span>
            <span className="text-slate-300">•</span>
            <span>
              {sessionDate.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true 
              })}
            </span>
          </div>
          <div className="text-right">
            <span className="text-xs text-slate-400">{totalMade}/{totalAttempted} shots</span>
          </div>
        </div>
        
        <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-100">
          <StatCell label="2PT" pct={twoPct} met={twoMet} made={session.two_pt_made} attempted={session.two_pt_attempted} />
          <StatCell label="3PT" pct={threePct} met={threeMet} made={session.three_pt_made} attempted={session.three_pt_attempted} />
          <StatCell label="FT" pct={ftPct} met={ftMet} made={session.ft_made} attempted={session.ft_attempted} />
        </div>
      </div>
    );
  };

  // Stat cell with goal indicator
  const StatCell = ({ label, pct, met, made, attempted }) => (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1">
        <p className={`text-lg font-bold ${
          met === null ? 'text-slate-400' : met ? 'text-emerald-600' : 'text-red-500'
        }`}>
          {pct !== null ? `${pct}%` : '-'}
        </p>
        {met !== null && (
          met ? (
            <Check className="w-4 h-4 text-emerald-500" />
          ) : (
            <X className="w-4 h-4 text-red-400" />
          )
        )}
      </div>
      <p className="text-xs text-slate-400">{label}</p>
      <p className="text-xs text-slate-300">{made}/{attempted}</p>
    </div>
  );

  return (
    <div data-testid="shot-counter-page">
      {/* Back to Practice */}
      <button
        onClick={() => viewMode === 'tracker' && sessionStarted ? setViewMode('dashboard') : navigate('/coach/lab')}
        data-testid="back-to-practice"
        className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-[11px] font-bold uppercase tracking-[.12em] mb-3 transition-colors"
        style={{ background: '#F1F5F9', color: '#64748B', border: '1px solid #E2E8F0', cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif" }}
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        {viewMode === 'tracker' ? 'Dashboard' : 'Back to Practice'}
      </button>

      {/* Unified page heading */}
      <h1
        className="text-2xl font-extrabold uppercase tracking-tight text-slate-900 mb-5"
        style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
        data-testid="shot-counter-title"
      >
        {viewMode === 'tracker' ? 'Shot Tracker' : 'Shot Counter'}
      </h1>

      {viewMode === 'dashboard' ? (
        /* ─── DASHBOARD VIEW ─── */
        <div className="max-w-lg mx-auto">
          {/* Goals Tile */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Target className="w-5 h-5 text-amber-500" />
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700">Practice Goals</h2>
              </div>
              <button 
                onClick={() => setShowGoalModal(true)}
                className="flex items-center gap-1 text-xs font-medium text-[#13314b] hover:text-[#13314b]"
                data-testid="edit-goals-btn"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{goals.two_pt_goal}%</p>
                <p className="text-xs text-slate-500 uppercase font-medium">2PT Goal</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{goals.three_pt_goal}%</p>
                <p className="text-xs text-slate-500 uppercase font-medium">3PT Goal</p>
              </div>
              <div className="text-center p-3 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{goals.ft_goal}%</p>
                <p className="text-xs text-slate-500 uppercase font-medium">FT Goal</p>
              </div>
            </div>
          </div>

          {/* Start Tracking — single centered pill, global standard */}
          <div className="flex justify-center mb-7">
            <Button
              onClick={startSession}
              className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[15px] font-semibold uppercase tracking-[.07em] gap-2 px-7 h-12"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              data-testid="start-session-btn"
            >
              <Play className="w-4 h-4" />
              Start Tracking
            </Button>
          </div>

          {/* Tracked Sessions */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2" data-testid="tracked-sessions-header">
              <Clock className="w-4 h-4" />
              Tracked Sessions
            </h3>
            
            {loadingSessions ? (
              <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
                <div className="animate-spin h-6 w-6 border-2 border-slate-300 border-t-slate-600 rounded-full mx-auto"></div>
              </div>
            ) : previousSessions.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl p-6 text-center">
                <p className="text-slate-400 text-sm">No tracked sessions yet</p>
                <p className="text-slate-300 text-xs mt-1">Tap Start Tracking to begin.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {previousSessions.map((session) => (
                  <SessionCard key={session.session_id} session={session} />
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* ─── SHOT TRACKER VIEW ─── */
        <div className="max-w-lg mx-auto">
          {/* Session Info */}
          {sessionStarted && sessionStartTime && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 mb-4 flex items-center justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Session Active</span>
                <p className="text-sm text-emerald-700">
                  Started {sessionStartTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={saveSession}
                  disabled={saving}
                  variant="outline"
                  size="sm"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  data-testid="save-session-btn"
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                  onClick={endSession}
                  size="sm"
                  className="bg-slate-800 hover:bg-slate-900 text-white"
                  data-testid="end-session-btn"
                >
                  End
                </Button>
              </div>
            </div>
          )}

          {/* Shot Counters */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm px-6 mb-6">
            <ShotRow 
              label="2PT" 
              made={stats.two_pt_made} 
              attempted={stats.two_pt_attempted}
              shotType="2PT"
            />
            <ShotRow 
              label="3PT" 
              made={stats.three_pt_made} 
              attempted={stats.three_pt_attempted}
              shotType="3PT"
            />
            <ShotRow 
              label="FT" 
              made={stats.ft_made} 
              attempted={stats.ft_attempted}
              shotType="FT"
            />
          </div>

          {/* Session Summary with Goal Comparison */}
          {(stats.two_pt_attempted > 0 || stats.three_pt_attempted > 0 || stats.ft_attempted > 0) && (
            <div className="bg-slate-800 text-white rounded-xl p-5">
              <h3 className="text-sm font-bold uppercase tracking-wider text-slate-400 mb-3">Session Summary</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                {[
                  { label: '2PT', made: stats.two_pt_made, attempted: stats.two_pt_attempted, goal: goals.two_pt_goal },
                  { label: '3PT', made: stats.three_pt_made, attempted: stats.three_pt_attempted, goal: goals.three_pt_goal },
                  { label: 'FT', made: stats.ft_made, attempted: stats.ft_attempted, goal: goals.ft_goal },
                ].map(({ label, made, attempted, goal }) => {
                  const pct = calcPct(made, attempted);
                  const met = meetsGoal(pct, goal);
                  return (
                    <div key={label}>
                      <div className="flex items-center justify-center gap-1">
                        <p className={`text-2xl font-bold ${
                          pct === null ? 'text-slate-400' : met ? 'text-emerald-400' : 'text-red-400'
                        }`}>
                          {pct !== null ? `${pct}%` : '-'}
                        </p>
                        {met !== null && (
                          met ? <Check className="w-4 h-4 text-emerald-400" /> : <X className="w-4 h-4 text-red-400" />
                        )}
                      </div>
                      <p className="text-xs text-slate-400 uppercase">{label}</p>
                      <p className="text-xs text-slate-500">Goal: {goal}%</p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-700 text-center">
                <p className="text-3xl font-bold">
                  {stats.two_pt_made + stats.three_pt_made + stats.ft_made}
                  <span className="text-slate-400 text-xl mx-1">/</span>
                  <span className="text-xl text-slate-400">
                    {stats.two_pt_attempted + stats.three_pt_attempted + stats.ft_attempted}
                  </span>
                </p>
                <p className="text-xs text-slate-400 uppercase mt-1">Total Shots</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Goals Modal */}
      <Dialog open={showGoalModal} onOpenChange={setShowGoalModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-amber-500" />
              Set Practice Goals
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-xs text-slate-500">
              Set your target shooting percentages. Sessions will show green (met) or red (not met) based on these goals.
            </p>
            
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                2-Point Goal (%)
              </label>
              <Input
                type="number"
                min="0"
                max="100"
                value={editingGoals.two_pt_goal}
                onChange={(e) => setEditingGoals(prev => ({ ...prev, two_pt_goal: parseInt(e.target.value) || 0 }))}
                className="h-11 text-lg font-semibold"
                data-testid="goal-2pt-input"
              />
            </div>
            
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                3-Point Goal (%)
              </label>
              <Input
                type="number"
                min="0"
                max="100"
                value={editingGoals.three_pt_goal}
                onChange={(e) => setEditingGoals(prev => ({ ...prev, three_pt_goal: parseInt(e.target.value) || 0 }))}
                className="h-11 text-lg font-semibold"
                data-testid="goal-3pt-input"
              />
            </div>
            
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                Free Throw Goal (%)
              </label>
              <Input
                type="number"
                min="0"
                max="100"
                value={editingGoals.ft_goal}
                onChange={(e) => setEditingGoals(prev => ({ ...prev, ft_goal: parseInt(e.target.value) || 0 }))}
                className="h-11 text-lg font-semibold"
                data-testid="goal-ft-input"
              />
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowGoalModal(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={saveGoals}
              disabled={savingGoals}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
              data-testid="save-goals-btn"
            >
              {savingGoals ? 'Saving...' : 'Save Goals'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
